// frontend/src/lib/typography.ts
// Typographic correctness for text the app does not author by hand: model prose,
// case YAML fields and docket numbers. The pixel face has no curly quotes, so
// pixel-face labels keep their straight marks; this is for the text face only.
// Idempotent: curly input passes through unchanged.

const NBSP = ' ';

/** The character before index i, looking past emphasis stars. */
function before(s: string, i: number): string {
  let j = i - 1;
  while (j >= 0 && s[j] === '*') j--;
  return j < 0 ? '' : s[j];
}

const OPENS = /^$|[\s([{‘“–\u2014]/;

/** Straight quotes and apostrophes to curly, three dots to an ellipsis. */
export function smartQuotes(s: string): string {
  if (!s || !/["'.]/.test(s)) return s;
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === '"') {
      out += OPENS.test(before(s, i)) ? '“' : '”';
    } else if (c === "'") {
      const prev = before(s, i);
      const next = s[i + 1] ?? '';
      // An opening single quote follows a space or an opening mark and leads a
      // word; a leading apostrophe ('90s, 'em) is the closing form.
      const opening = OPENS.test(prev) && /[A-Za-z]/.test(next) && !/^(?:em|tis|twas)\b/i.test(s.slice(i + 1, i + 6));
      out += opening ? '‘' : '’';
    } else if (c === '.' && s[i + 1] === '.' && s[i + 2] === '.') {
      out += '…';
      i += 2;
    } else {
      out += c;
    }
  }
  return out;
}

/** Hard spaces where a break would split a citation: "U. S.", "No. 22-451", "§ 1983", "v. Name". */
export function bindTerms(s: string): string {
  if (!s) return s;
  return s
    .replace(/\bU\.\s?S\.(?=\s|$|[,;:)])/g, `U.${NBSP}S.`)
    .replace(/\b(Nos?\.)\s(?=\d)/g, `$1${NBSP}`)
    .replace(/([§¶])\s(?=\d)/g, `$1${NBSP}`)
    .replace(/\b(v\.)\s(?=\S)/g, `$1${NBSP}`);
}

/** Quotes, ellipses and hard spaces in one pass. */
export function typeset(s: string): string {
  return bindTerms(smartQuotes(s));
}

/** `07-290` to `07–290`: the Court's docket numbers take an en dash. */
export function docketNumber(docket: string | null | undefined): string {
  if (!docket) return '';
  return docket.replace(/(\d)-(?=\d)/g, '$1–');
}

/** `No. 07–290`, never split across a line. */
export function docketLabel(docket: string | null | undefined): string {
  return `No.${NBSP}${docketNumber(docket) || 'n/a'}`;
}

const KEEP = new Set(['id', 'slug', 'url', 'href', 'key', 'kind', 'icon', 'audio', 'voice']);

/** Curl the quotes in every display string of a catalogue response (cases, briefs, achievements). */
export function curlText<T>(value: T, key = ''): T {
  if (typeof value === 'string') {
    if (key === 'vote') return docketNumber(smartQuotes(value)) as T; // 5-4 is a range: en dash
    return (KEEP.has(key) || /(?:^|_)(?:id|slug|url)$/i.test(key) ? value : smartQuotes(value)) as T;
  }
  if (Array.isArray(value)) return value.map((v) => curlText(v, key)) as T;
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, curlText(v, k)])) as T;
  }
  return value;
}
